import { useRef, useState } from 'react'

import { BgColorsOutlined } from '@ant-design/icons'
import { useBoolean } from 'ahooks'
import { Button, ColorPicker, Divider, Space } from 'antd'
import { createStyles } from 'antd-style'
import { $getSelection } from 'lexical'

import { getFirstScrolledParent } from '@eastcoal/common/utils/dom'

import { useEditor } from '../../hooks/useEditor'
import { useTableForced } from '../../hooks/useTableForced'
import { ColorCursor } from '../../ui/ColorCursor'
import { DropdownButton } from '../../ui/DropdownButton'
import { MenuButton } from '../../ui/MenuButton'
import { $getTableCellNodes } from '../../utils/table'

const presetColors = [
  'rgb(255, 255, 0)',
  'rgb(0, 255, 0)',
  'rgb(0, 255, 255)',
  'rgb(255, 0, 255)',
  'rgb(0, 0, 255)',
  'rgb(255, 0, 0)',
  'rgb(0, 0, 128)',
  'rgb(0, 128, 128)',
  'rgb(0, 128, 0)',
  'rgb(128, 0, 128)',
  'rgb(128, 0, 0)',
  'rgb(128, 128, 0)',
  'rgb(128, 128, 128)',
  'rgb(192, 192, 192)',
  'rgb(0, 0, 0)'
]

const presets = [
  {
    label: '预设颜色',
    colors: presetColors
  }
]

const useStyles = createStyles(({ token }) => ({
  container: {
    borderRadius: token.borderRadiusSM,
    [`&:hover`]: {
      backgroundColor: token.colorBgTextHover
    }
  },
  clearBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    lineHeight: 1,
    width: '100%'
  },
  clearIcon: {
    position: 'relative',
    display: 'inline-block',
    width: 18,
    height: 18,
    borderRadius: token.borderRadiusSM,
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorder}`,
    cursor: 'pointer',
    overflow: 'hidden',

    [`&::after`]: {
      content: '""',
      position: 'absolute',
      insetInlineEnd: 1,
      top: 0,
      display: 'block',
      width: 40,
      height: 2,
      transformOrigin: 'right',
      transform: 'rotate(-45deg)',
      backgroundColor: token.red
    }
  },
  panel: {
    width: 360
  }
}))

export const TableCellBackgroundColorButton = () => {
  const editor = useEditor()
  const [backgroundColor, setBackgroundColor] = useState('')
  const activeBackgroundColorRef = useRef(backgroundColor)

  const setBackgroundColorStyle = (color: string) => {
    editor.update(() => {
      const selection = $getSelection()
      const cellNodes = $getTableCellNodes(selection)
      cellNodes.forEach(cell => {
        cell.setBackgroundColor(color)
      })
    })
  }

  const toggleConfirmBackgroundColor = () => {
    activeBackgroundColorRef.current = activeBackgroundColorRef.current
      ? ''
      : backgroundColor
    setBackgroundColorStyle(activeBackgroundColorRef.current)
  }

  const handleClearBackgroundColor = () => {
    handleColorPickerChange(undefined, '')
  }

  const handleColorPickerChange = (_: unknown, hex: string) => {
    activeBackgroundColorRef.current = hex
    setBackgroundColorStyle(hex)
    setBackgroundColor(hex)
  }

  const tableForced = useTableForced()
  const [open, openActions] = useBoolean(false)
  const { styles } = useStyles()

  return tableForced ? (
    <Space.Compact className={styles.container}>
      <MenuButton tooltip='单元格背景色' onClick={toggleConfirmBackgroundColor}>
        <BgColorsOutlined />
        <ColorCursor color={backgroundColor} />
      </MenuButton>
      <ColorPicker
        arrow={false}
        getPopupContainer={triggerNode => getFirstScrolledParent(triggerNode)}
        open={open}
        placement='bottomRight'
        presets={presets}
        value={backgroundColor}
        panelRender={(_, { components: { Picker, Presets } }) => (
          <div className={styles.panel}>
            <Button
              className={styles.clearBtn}
              icon={<span className={styles.clearIcon} />}
              type='text'
              onClick={handleClearBackgroundColor}
            >
              无填充色
            </Button>
            <Divider orientation='horizontal' style={{ margin: '8px 0' }} />
            <Presets />
            <Picker />
          </div>
        )}
        onChange={handleColorPickerChange}
        onChangeComplete={openActions.setFalse}
        onOpenChange={openActions.set}
      >
        <DropdownButton compact />
      </ColorPicker>
    </Space.Compact>
  ) : null
}
